import { forwardRef, useCallback, useEffect, useState } from 'react';
import { PosTextField } from 'src/core/POS/PosTextField';
import { Slider } from 'src/core/ui/Slider';

import * as styles from './TargetRoasInput.css';

const DEFAULT_MIN = 1;
const DEFAULT_RANGE_START = 5;
const DEFAULT_RANGE_END = 200;

type TargetRoasInputProps = {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  rangeStart?: number;
  rangeEnd?: number;
  disabled?: boolean;
  error?: string;
};

export const TargetRoasInput = forwardRef<HTMLDivElement, TargetRoasInputProps>(
  function TargetRoasInput(
    {
      value,
      onChange,
      min = DEFAULT_MIN,
      rangeStart = DEFAULT_RANGE_START,
      rangeEnd = DEFAULT_RANGE_END,
      disabled = false,
      error,
    },
    ref
  ) {
    // Dynamic slider range - extends if text input value exceeds current range
    const [dynamicRangeEnd, setDynamicRangeEnd] = useState(
      Math.max(rangeEnd, value)
    );
    const [dynamicRangeStart, setDynamicRangeStart] = useState(
      Math.min(rangeStart, value)
    );

    const [textValue, setTextValue] = useState(String(value));

    // Sync internal text state when the controlled value prop changes
    // (e.g. when switching bid modes repopulates the ROAS value).
    useEffect(() => {
      setTextValue(String(value));
    }, [value]);

    const handleSliderChange = useCallback(
      (values: number[]) => {
        const newValue = values[0];
        setTextValue(String(newValue));
        onChange(newValue);
      },
      [onChange]
    );

    const handleTextChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setTextValue(e.target.value);
      },
      []
    );

    const handleTextBlur = useCallback(() => {
      const parsed = parseInt(textValue, 10);
      if (isNaN(parsed) || parsed < min) {
        // Reset to current value if invalid
        setTextValue(String(value));
        return;
      }

      // Extend slider range if needed
      if (parsed > dynamicRangeEnd) {
        setDynamicRangeEnd(Math.ceil(parsed / 5) * 5); // Round to nearest 5
      }
      if (parsed < dynamicRangeStart) {
        setDynamicRangeStart(Math.max(min, Math.floor(parsed / 5) * 5));
      }

      setTextValue(String(parsed));
      onChange(parsed);
    }, [textValue, min, value, onChange, dynamicRangeEnd, dynamicRangeStart]);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
          (e.target as HTMLInputElement).blur();
        }
      },
      []
    );

    return (
      <div ref={ref} className={styles.container}>
        <div className={styles.inputRow}>
          <div className={styles.sliderContainer}>
            <Slider.Root
              value={[value]}
              min={dynamicRangeStart}
              max={dynamicRangeEnd}
              step={1}
              disabled={disabled}
              onValueChange={handleSliderChange}
            />
          </div>
          <div className={styles.textInputContainer}>
            <PosTextField
              type="number"
              inputMode="numeric"
              value={textValue}
              onChange={handleTextChange}
              onBlur={handleTextBlur}
              onKeyDown={handleKeyDown}
              disabled={disabled}
              postfixDisplay={'\u00d7'}
            />
          </div>
        </div>
        {error ? (
          <p className={styles.errorText}>{error}</p>
        ) : (
          <p className={styles.helperText}>
            For every $1 you spend on ads, you&apos;re targeting ${value} in
            sales.
          </p>
        )}
      </div>
    );
  }
);
